import React, { useState } from "react";
import { CheckCircle2, AlertTriangle, XCircle, Link2, Layers, Trash2, Plus } from "lucide-react";

const labelStyles = {
  high: "border-emerald-300 text-emerald-700 bg-emerald-50 dark:border-emerald-800 dark:text-emerald-400 dark:bg-emerald-950/40",
  medium: "border-amber-300 text-amber-700 bg-amber-50 dark:border-amber-800 dark:text-amber-400 dark:bg-amber-950/40",
  low: "border-red-300 text-red-700 bg-red-50 dark:border-red-900 dark:text-red-400 dark:bg-red-950/40",
};

const statusMeta = {
  generated: { icon: CheckCircle2, cls: "text-emerald-600 dark:text-emerald-400", text: "Generated" },
  partial: { icon: AlertTriangle, cls: "text-amber-600 dark:text-amber-400", text: "Partial" },
  skipped: { icon: XCircle, cls: "text-zinc-400", text: "Skipped" },
};

export const ComponentCard = ({ comp, validation, onUpdateRound, onAddRound, onDeleteRound, onRefine, isRefining, mode }) => {
  const [refineText, setRefineText] = useState("");
  const label = (comp.confidence_label || "low").toLowerCase();
  const status = comp.generation_status || "skipped";
  const meta = statusMeta[status] || statusMeta.skipped;
  const StatusIcon = meta.icon;
  const invalid = validation && validation.is_valid === false;

  const handleRefineSubmit = () => {
    if (!refineText.trim()) return;
    onRefine(comp.component_id, refineText);
    setRefineText("");
  };

  return (
    <div
      data-testid={`component-card-${comp.component_id}`}
      className={`rounded-xl border p-4 flex flex-col gap-3 transition-colors ${
        status === "skipped"
          ? "border-dashed border-zinc-300 dark:border-zinc-700 bg-zinc-100/60 dark:bg-zinc-900/30"
          : "border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/50"
      } ${isRefining ? "opacity-75" : ""}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <h4 className="font-display font-semibold text-base leading-tight text-zinc-900 dark:text-zinc-50">
            {comp.component_name}
          </h4>
          <p className="text-xs text-zinc-400 mt-0.5 capitalize">
            {comp.component_group} · {comp.shape_type}
          </p>
        </div>
        <span className={`shrink-0 inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${labelStyles[label]}`}>
          {label} · {Number(comp.confidence_score).toFixed(2)}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-zinc-500 dark:text-zinc-400">
        <span className={`inline-flex items-center gap-1 ${meta.cls} font-medium`}>
          <StatusIcon className="h-3.5 w-3.5" /> {meta.text}
        </span>
        <span className="inline-flex items-center gap-1">
          <Layers className="h-3.5 w-3.5" /> Count: {comp.count_estimate}
        </span>
        {comp.is_repeated_pattern_candidate && (
          <span className="inline-flex items-center gap-1 text-zinc-600 dark:text-zinc-300">
            <Link2 className="h-3.5 w-3.5" /> Shared pattern (est.)
          </span>
        )}
      </div>

      {comp.visibility_notes && (
        <p className="text-xs leading-relaxed text-zinc-600 dark:text-zinc-300">{comp.visibility_notes}</p>
      )}

      {Array.isArray(comp.uncertainty_reasons) && comp.uncertainty_reasons.length > 0 && (
        <div className="flex flex-col gap-1">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-400">Uncertainty</p>
          <ul className="list-disc list-inside text-xs text-zinc-500 dark:text-zinc-400 space-y-0.5">
            {comp.uncertainty_reasons.map((r, i) => (
              <li key={`${comp.component_id}-reason-${i}`}>{r}</li>
            ))}
          </ul>
        </div>
      )}

      {invalid && validation.errors?.length > 0 && (
        <div className="rounded-lg border border-red-200 bg-red-50 dark:border-red-900/60 dark:bg-red-950/30 px-3 py-2">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-red-600 dark:text-red-400 mb-1">
            Draft withheld — validation failed
          </p>
          <ul className="list-disc list-inside text-xs text-red-600 dark:text-red-400 space-y-0.5">
            {validation.errors.map((e, i) => (
              <li key={`${comp.component_id}-err-${i}`}>{e}</li>
            ))}
          </ul>
        </div>
      )}

      {status === "skipped" && (
        <div
          data-testid={`skipped-note-${comp.component_id}`}
          className="rounded-lg border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/40 px-3 py-2.5 flex items-start gap-2"
        >
          <XCircle className="h-4 w-4 text-zinc-400 mt-0.5 shrink-0" />
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
              No pattern generated
            </p>
            <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5 leading-relaxed">
              {comp.pattern_notes || "Skipped — insufficient visibility or confidence to draft a reliable pattern."}
            </p>
          </div>
        </div>
      )}

      {!invalid && status !== "skipped" && Array.isArray(comp.rounds) && comp.rounds.length > 0 && (
        <div className="flex flex-col gap-2 mt-1">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-400">
            {mode === "garment" ? "Rows & Stitch Counts" : "Rounds & Stitch Counts"}
          </p>
          <div className="flex flex-col gap-1.5 max-h-60 overflow-y-auto pr-1">
            {comp.rounds.map((r) => (
              <div key={`${comp.component_id}-round-${r.round_number}`} className="rounded-md bg-zinc-50 dark:bg-zinc-900/60 p-2 text-xs">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-zinc-400 w-12 shrink-0">
                    {mode === "garment" ? "Row" : "Rnd"} {r.round_number}:
                  </span>
                  <input
                    data-testid={`round-sequence-${comp.component_id}-${r.round_number}`}
                    type="text"
                    value={r.stitch_sequence}
                    disabled={isRefining}
                    onChange={(e) => onUpdateRound(comp.component_id, r.round_number, "stitch_sequence", e.target.value)}
                    className="flex-1 min-w-0 bg-zinc-100 dark:bg-zinc-800 border border-transparent focus:border-zinc-300 dark:focus:border-zinc-700 rounded px-2 py-0.5 font-mono text-xs focus:outline-none"
                  />
                  <input
                    data-testid={`round-count-${comp.component_id}-${r.round_number}`}
                    type="number"
                    min="1"
                    value={r.total_count}
                    disabled={isRefining}
                    aria-label={`Total stitches for ${mode === "garment" ? "row" : "round"} ${r.round_number}`}
                    onChange={(e) => onUpdateRound(comp.component_id, r.round_number, "total_count", e.target.value)}
                    className="w-14 bg-zinc-100 dark:bg-zinc-800 border border-transparent focus:border-zinc-300 dark:focus:border-zinc-700 rounded px-2 py-0.5 font-mono text-xs text-right focus:outline-none"
                  />
                  <button
                    data-testid={`delete-round-${comp.component_id}-${r.round_number}`}
                    disabled={isRefining}
                    onClick={() => onDeleteRound(comp.component_id, r.round_number)}
                    className="p-1 hover:bg-zinc-200 dark:hover:bg-zinc-800 text-zinc-400 hover:text-red-500 rounded transition-colors disabled:opacity-40 shrink-0"
                    title="Delete Round"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
                <div className="mt-2 ml-14 flex flex-wrap items-center gap-2 text-[10px] text-zinc-500 dark:text-zinc-400">
                  <select
                    value={r.work_style || (mode === "garment" ? "flat" : "continuous")}
                    disabled={isRefining}
                    aria-label={`Construction style for ${mode === "garment" ? "row" : "round"} ${r.round_number}`}
                    onChange={(e) => onUpdateRound(comp.component_id, r.round_number, "work_style", e.target.value)}
                    className="rounded border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 px-1.5 py-0.5"
                  >
                    <option value="continuous">continuous</option>
                    <option value="joined">joined</option>
                    <option value="flat">flat</option>
                  </select>
                  <label className="inline-flex items-center gap-1">
                    ch
                    <input
                      type="number"
                      min="0"
                      value={r.starting_chain_count ?? 0}
                      disabled={isRefining}
                      aria-label={`Starting chain count for ${mode === "garment" ? "row" : "round"} ${r.round_number}`}
                      onChange={(e) => onUpdateRound(comp.component_id, r.round_number, "starting_chain_count", e.target.value)}
                      className="w-10 rounded border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 px-1 py-0.5 text-right"
                    />
                  </label>
                  <label className="inline-flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={r.starting_chain_counts_as_stitch || false}
                      disabled={isRefining || !(r.starting_chain_count > 0)}
                      onChange={(e) => onUpdateRound(comp.component_id, r.round_number, "starting_chain_counts_as_stitch", e.target.checked)}
                    />
                    chain counts as first stitch
                  </label>
                  <label className="inline-flex items-center gap-1">
                    join
                    <select
                      value={r.join_method || "none"}
                      disabled={isRefining}
                      aria-label={`Join method for ${mode === "garment" ? "row" : "round"} ${r.round_number}`}
                      onChange={(e) => onUpdateRound(comp.component_id, r.round_number, "join_method", e.target.value)}
                      className="rounded border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 px-1.5 py-0.5"
                    >
                      <option value="none">none</option>
                      <option value="slst">slst (uncounted)</option>
                      <option value="other">other</option>
                    </select>
                  </label>
                </div>
                <input
                  type="text"
                  value={r.beginner_instruction || ""}
                  disabled={isRefining}
                  aria-label={`Beginner instruction for ${mode === "garment" ? "row" : "round"} ${r.round_number}`}
                  placeholder="Optional plain-language beginner instruction"
                  onChange={(e) => onUpdateRound(comp.component_id, r.round_number, "beginner_instruction", e.target.value)}
                  className="mt-2 ml-14 w-[calc(100%_-_3.5rem)] bg-transparent border-b border-zinc-200 dark:border-zinc-800 px-1 py-0.5 text-[11px] focus:outline-none focus:border-zinc-400"
                />
              </div>
            ))}
          </div>
          <button
            data-testid={`add-round-${comp.component_id}`}
            disabled={isRefining}
            onClick={() => onAddRound(comp.component_id)}
            className="self-start mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-50 transition-colors disabled:opacity-40"
          >
            <Plus className="h-3.5 w-3.5" /> {mode === "garment" ? "Add Row" : "Add Round"}
          </button>
        </div>
      )}

      {status !== "skipped" && label === "low" && !invalid && (
        <p className="text-xs italic text-amber-600 dark:text-amber-400">Low confidence — manual refinement recommended.</p>
      )}

      {/* Refine with AI Bubble */}
      <div className="flex flex-col gap-2 border-t border-zinc-100 dark:border-zinc-800/80 pt-3 mt-1">
        <label className="text-[11px] font-semibold uppercase tracking-wide text-zinc-400">Refine with AI</label>
        <div className="flex gap-2">
          <textarea
            data-testid={`refine-input-${comp.component_id}`}
            rows={1}
            disabled={isRefining}
            placeholder={isRefining ? "Refining..." : "e.g. make it a sphere rather than an oval"}
            value={refineText}
            onChange={(e) => setRefineText(e.target.value)}
            className="flex-1 bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200 dark:border-zinc-800 focus:border-zinc-400 dark:focus:border-zinc-700 rounded-md px-3 py-1.5 text-xs focus:outline-none resize-none"
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleRefineSubmit();
              }
            }}
          />
          <button
            data-testid={`refine-submit-${comp.component_id}`}
            disabled={isRefining || !refineText.trim()}
            onClick={handleRefineSubmit}
            className="shrink-0 bg-zinc-900 text-zinc-50 dark:bg-zinc-100 dark:text-zinc-900 px-3 py-1.5 rounded-md text-xs font-semibold hover:bg-zinc-800 dark:hover:bg-white transition-colors disabled:opacity-40"
          >
            {isRefining ? "Refining..." : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
};
